import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { BaseLead } from "@/types/lead";
import { useMediaQuery } from "@/hooks/use-mobile";
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTheme } from "@/context/ThemeContext";
import { Pagination } from "@/components/ui/pagination";
import { getLanguage } from "@/utils/languageUtils";
import {
  leadStatusLabels,
  labelToLeadStatus,
  leadDelayedLabel,
  leadUILabels,
  commonMiscLabels,
} from "@/i18n";
import { transmissionTypeLabels } from "@/i18n/labels/vehicleLabels";
import { TransmissionType, LeadStatus } from "@/types/enums";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, X, MessageSquare, Loader2 } from "lucide-react";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { LeadService } from "@/services/lead/leadService";
import type { LeadDetails } from "@/types/lead";
import { format, parse } from "date-fns";

import { NativeListCard } from "@/components/native/NativeListCard";
import { useNativePlatform } from "@/hooks/useNativePlatform";

interface LeadTableNewProps {
  leads: BaseLead[];
  onLeadClick: (id: string) => void;
  vehicleType: "car" | "motorcycle";
  onSearch?: (query: string) => void;
  isSearching?: boolean;
  totalLeads?: number;
  onStatusesChange?: (statuses: string[]) => void;
  onPageChange?: (page: number) => void;
  onItemsPerPageChange?: (itemsPerPage: number) => void;
  selectedStatuses?: string[];
  currentPage?: number;
  itemsPerPage?: number;
  searchQuery?: string;
  onSearchQueryChange?: (query: string) => void;
}

/** Hover card that fetches and shows lead comments when hovering over the notes icon */
function LeadCommentsHoverCell({
  leadId,
  notesCount,
}: {
  leadId: string;
  notesCount?: number;
}) {
  const [open, setOpen] = useState(false);
  const { data: lead, isLoading } = useQuery({
    queryKey: ["lead", leadId],
    queryFn: () => LeadService.getLead(leadId),
    enabled: open && !!leadId,
    // Never serve a lead from cache — see the ruling in useLead.ts.
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
  });
  const comments = (lead as LeadDetails)?.comments ?? [];
  const userComment = (lead as LeadDetails)?.userComment;
  const hasAny = !!userComment || comments.length > 0;

  return (
    <HoverCard
      open={open}
      onOpenChange={setOpen}
      openDelay={0}
      closeDelay={100}
    >
      <HoverCardTrigger asChild>
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-accent/30 cursor-default">
          <MessageSquare className="h-4 w-4 text-muted-foreground" />
          {typeof notesCount === "number" && notesCount > 0 && (
            <span className="ml-0.5 text-xs">({notesCount})</span>
          )}
        </span>
      </HoverCardTrigger>
      <HoverCardContent
        side="top"
        align="end"
        sideOffset={8}
        className="w-80 max-h-[280px] overflow-y-auto z-[230]"
      >
        {isLoading ? (
          <div
            className="flex items-center justify-center py-8 min-h-[80px]"
            aria-busy="true"
          >
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : !hasAny ? (
          <p className="text-sm text-muted-foreground">
            {leadUILabels(getLanguage()).noNotes}
          </p>
        ) : (
          <div className="space-y-3">
            {userComment && (
              <div className="rounded-md bg-muted/50 p-2">
                <p className="text-xs font-medium text-muted-foreground mb-1">
                  {leadUILabels(getLanguage()).customerComment}
                </p>
                <p className="text-sm whitespace-pre-wrap">{userComment}</p>
              </div>
            )}
            {comments.map(
              (c: {
                id?: string;
                author?: string;
                text?: string;
                timestamp?: string | Date;
              }) => (
                <div key={c.id ?? String(c)} className="rounded-md border p-2">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-xs font-medium">
                      {c.author ?? "—"}
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                      {c.timestamp
                        ? format(
                            typeof c.timestamp === "string"
                              ? new Date(c.timestamp)
                              : c.timestamp,
                            "dd/MM HH:mm",
                          )
                        : ""}
                    </span>
                  </div>
                  <p className="text-sm whitespace-pre-wrap">{c.text ?? ""}</p>
                </div>
              ),
            )}
          </div>
        )}
      </HoverCardContent>
    </HoverCard>
  );
}

export const LeadTableNew = ({
  leads,
  onLeadClick,
  vehicleType,
  onSearch,
  isSearching = false,
  totalLeads = 0,
  onStatusesChange,
  onPageChange,
  onItemsPerPageChange,
  selectedStatuses = [],
  currentPage: propCurrentPage,
  itemsPerPage: propItemsPerPage,
  searchQuery: propSearchQuery = "",
  onSearchQueryChange,
}: LeadTableNewProps) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const uiLabels = leadUILabels(getLanguage());
  const commonLabels = commonMiscLabels(getLanguage());
  const [currentPage, setCurrentPage] = useState(propCurrentPage || 1);
  const [searchInput, setSearchInput] = useState(propSearchQuery || "");
  const [itemsPerPage, setItemsPerPage] = useState(propItemsPerPage || 10);
  const selectedSet = new Set(selectedStatuses);

  // Sync searchInput with prop
  useEffect(() => {
    setSearchInput(propSearchQuery || "");
  }, [propSearchQuery]);

  // Sync with props
  useEffect(() => {
    if (propCurrentPage !== undefined) {
      setCurrentPage(propCurrentPage);
    }
  }, [propCurrentPage]);

  useEffect(() => {
    if (propItemsPerPage !== undefined) {
      setItemsPerPage(propItemsPerPage);
    }
  }, [propItemsPerPage]);

  const toggleStatus = (value: string) => {
    if (!onStatusesChange) return;
    if (value === "all") {
      onStatusesChange([]);
      return;
    }
    const reverseMap = labelToLeadStatus(getLanguage());
    const technical = reverseMap[value] ?? value;
    const next = selectedSet.has(technical)
      ? selectedStatuses.filter((s) => s !== technical)
      : [...selectedStatuses, technical];
    onStatusesChange(next);
  };

  const handleSearch = () => {
    if (onSearchQueryChange) {
      onSearchQueryChange(searchInput.trim());
    }
    if (onSearch) {
      onSearch(searchInput.trim());
    }
  };

  const handleClearSearch = () => {
    setSearchInput("");
    if (onSearchQueryChange) {
      onSearchQueryChange("");
    }
    if (onSearch) {
      onSearch("");
    }
  };

  const getStatusColor = (status: string) => {
    const statusLabels = leadStatusLabels(getLanguage());
    switch (status) {
      case statusLabels.CONVERTED:
        return isDark
          ? "bg-green-950 text-green-100 hover:bg-green-700"
          : "bg-green-50 text-green-600 hover:bg-green-100";
      case statusLabels.CONTACTED:
        return isDark
          ? "bg-blue-950 text-blue-100 hover:bg-blue-700"
          : "bg-blue-50 text-blue-600 hover:bg-blue-100";
      case statusLabels.FOLLOW_UP:
        return isDark
          ? "bg-sky-950 text-sky-100 hover:bg-sky-700"
          : "bg-sky-50 text-sky-600 hover:bg-sky-100";
      case statusLabels.NEW:
        return isDark
          ? "bg-yellow-950 text-yellow-100 hover:bg-yellow-600"
          : "bg-yellow-50 text-yellow-600 hover:bg-yellow-100";
      case statusLabels.LOST:
        return isDark
          ? "bg-red-950 text-red-100 hover:bg-red-700"
          : "bg-red-50 text-red-600 hover:bg-red-100";
      default:
        return isDark
          ? "bg-gray-950 text-gray-100 hover:bg-gray-600"
          : "bg-gray-50 text-gray-600 hover:bg-gray-100";
    }
  };

  /** True if lead is in NEW status and created more than 24h ago (show "Försenad" badge). */
  const isLeadDelayed = (lead: BaseLead): boolean => {
    if ((lead.status ?? "") !== LeadStatus.NEW) return false;
    let created: Date;
    if (lead.createdAt) {
      created = new Date(lead.createdAt);
    } else if (lead.timestamp) {
      // API may only send timestamp as "dd/MM HH:mm"; new Date("19/02 21:59") is Invalid in JS
      try {
        created = parse(lead.timestamp.trim(), "dd/MM HH:mm", new Date());
      } catch {
        created = new Date(lead.timestamp);
      }
    } else {
      return false;
    }
    if (isNaN(created.getTime())) return false;
    return Date.now() - created.getTime() > 24 * 60 * 60 * 1000;
  };

  const getTotalPages = () => {
    return Math.ceil(totalLeads / itemsPerPage);
  };

  const renderTable = () => {
    const totalPages = getTotalPages();
    const { isNative } = useNativePlatform();

    if (isNative) {
      return (
        <div className="space-y-4">
          <div className="space-y-2">
            {leads.map((lead) => {
              const displayName =
                (lead.userName && lead.userName.trim()) ||
                (lead.firstName && lead.lastName
                  ? `${lead.firstName} ${lead.lastName}`.trim()
                  : null) ||
                lead.email;
              const statusLabel =
                leadStatusLabels(getLanguage())[
                  lead.status as LeadStatus
                ] || lead.status;
              const delayed = isLeadDelayed(lead);
              return (
                <NativeListCard
                  key={lead.id}
                  title={
                    <span className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="rounded-sm">
                        {lead.regNumber}
                      </Badge>
                      {`${lead.year} ${lead.make} ${lead.model}`}
                    </span>
                  }
                  subtitle={displayName}
                  meta={lead.timestamp}
                  badges={
                    <>
                      <Badge
                        className={`rounded-sm text-[10px] ${getStatusColor(statusLabel)}`}
                      >
                        {statusLabel}
                      </Badge>
                      {delayed && (
                        <Badge
                          variant="outline"
                          className="rounded-sm text-[10px] border-amber-500 text-amber-600"
                        >
                          {leadDelayedLabel(getLanguage())}
                        </Badge>
                      )}
                    </>
                  }
                  onClick={() => onLeadClick(lead.id)}
                />
              );
            })}
          </div>
          {totalPages > 1 && (
            <div className="flex justify-center">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalLeads}
                itemsPerPage={itemsPerPage}
                onPageChange={(page) => {
                  setCurrentPage(page);
                  onPageChange?.(page);
                }}
                onItemsPerPageChange={(newItemsPerPage) => {
                  setItemsPerPage(newItemsPerPage);
                  setCurrentPage(1);
                  onItemsPerPageChange?.(newItemsPerPage);
                }}
              />
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{uiLabels.tableRegNr}</TableHead>
              <TableHead>
                {vehicleType === "car" ? uiLabels.tableCar : uiLabels.tableMc}
              </TableHead>
              <TableHead>{uiLabels.tableTransmission}</TableHead>
              {/* <TableHead>Plats</TableHead> */}
              <TableHead>{uiLabels.tableName}</TableHead>
              <TableHead>{uiLabels.tableCreated}</TableHead>
              <TableHead>{uiLabels.tableStatus}</TableHead>
              <TableHead className="w-10 text-right">
                {uiLabels.tableNotes}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {leads.map((lead) => {
              const displayName =
                (lead.userName && lead.userName.trim()) ||
                (lead.firstName && lead.lastName
                  ? `${lead.firstName} ${lead.lastName}`.trim()
                  : null) ||
                lead.email;
              return (
                <TableRow
                  key={lead.id}
                  className="group cursor-pointer hover:bg-accent/20 transition-colors"
                  onClick={() => {
                    onLeadClick(lead.id);
                  }}
                >
                  <TableCell>
                    <Badge variant="outline" className="rounded-sm">
                      {lead.regNumber}
                    </Badge>
                  </TableCell>
                  <TableCell>{`${lead.year} ${lead.make} ${lead.model}`}</TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="rounded-sm">
                      {transmissionTypeLabels(getLanguage())[
                        lead.transmission as TransmissionType
                      ] ?? lead.transmission}
                    </Badge>
                  </TableCell>
                  {/* <TableCell>{lead.city}</TableCell> */}
                  <TableCell>{displayName}</TableCell>
                  <TableCell className="font-mono">{lead.timestamp}</TableCell>
                  <TableCell>
                    {(() => {
                      const labels = leadStatusLabels(getLanguage());
                      const statusLabel =
                        labels[lead.status as LeadStatus] || lead.status;
                      const delayed = isLeadDelayed(lead);
                      const delayedLabel = leadDelayedLabel(getLanguage());
                      return (
                        <span className="inline-flex flex-wrap items-center gap-1">
                          <Badge
                            className={`rounded-sm ${getStatusColor(statusLabel)}`}
                          >
                            {statusLabel}
                          </Badge>
                          {delayed && (
                            <Badge
                              variant="outline"
                              className="rounded-sm text-[10px] px-1.5 py-0 border-amber-500 text-amber-600 dark:text-amber-400 dark:border-amber-400"
                            >
                              {delayedLabel}
                            </Badge>
                          )}
                        </span>
                      );
                    })()}
                  </TableCell>
                  <TableCell
                    className="text-right"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <LeadCommentsHoverCell
                      leadId={lead.id}
                      notesCount={lead.notesCount}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        {totalPages > 1 && (
          <div className="flex justify-center">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalLeads}
              itemsPerPage={itemsPerPage}
              onPageChange={(page) => {
                setCurrentPage(page);
                if (onPageChange) {
                  onPageChange(page);
                }
              }}
              onItemsPerPageChange={(newItemsPerPage) => {
                setItemsPerPage(newItemsPerPage);
                setCurrentPage(1);
                if (onItemsPerPageChange) {
                  onItemsPerPageChange(newItemsPerPage);
                }
              }}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:flex-wrap">
        <div className="flex w-full min-w-0 flex-1 gap-2 sm:min-w-[200px]">
          <Input
            placeholder={uiLabels.searchPlaceholder}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleSearch()}
            className="min-w-0 flex-1"
          />
          <Button
            onClick={handleSearch}
            disabled={isSearching || !searchInput.trim()}
            size="sm"
            className="shrink-0"
            aria-label={
              isSearching ? commonLabels.searching : commonLabels.search
            }
            title={
              isSearching ? commonLabels.searching : commonLabels.search
            }
          >
            <Search className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">
              {isSearching ? commonLabels.searching : commonLabels.search}
            </span>
          </Button>
          {propSearchQuery && (
            <Button
              onClick={handleClearSearch}
              variant="outline"
              size="sm"
              className="shrink-0"
              aria-label={uiLabels.clear}
              title={uiLabels.clear}
            >
              <X className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{uiLabels.clear}</span>
            </Button>
          )}
        </div>
        {totalLeads > 0 && (
          <div className="text-sm text-muted-foreground sm:ml-auto">
            {commonLabels.showing} {leads.length} {commonLabels.of} {totalLeads}{" "}
            {uiLabels.leads}
          </div>
        )}
      </div>

      {/* Multi-select status filters */}
      <div className="-mx-1 flex gap-2 overflow-x-auto overflow-y-hidden px-1 pb-1 touch-pan-x sm:mx-0 sm:flex-wrap sm:overflow-visible">
        {(() => {
          const statusLabels = leadStatusLabels(getLanguage());
          const reverseMap = labelToLeadStatus(getLanguage());
          const options = [
            { value: "all", label: commonLabels.all, technical: null as string | null },
            { value: statusLabels.NEW, label: statusLabels.NEW, technical: "NEW" },
            { value: statusLabels.CONTACTED, label: statusLabels.CONTACTED, technical: "CONTACTED" },
            { value: statusLabels.FOLLOW_UP, label: statusLabels.FOLLOW_UP, technical: "FOLLOW_UP" },
            { value: statusLabels.CONVERTED, label: statusLabels.CONVERTED, technical: "CONVERTED" },
            { value: statusLabels.LOST, label: statusLabels.LOST, technical: "LOST" },
          ];
          return options.map((opt) => {
            const isAll = opt.value === "all";
            const isActive = isAll ? selectedStatuses.length === 0 : (opt.technical != null && selectedSet.has(opt.technical));
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  toggleStatus(opt.value);
                  setCurrentPage(1);
                  if (onPageChange) onPageChange(1);
                }}
                className={`shrink-0 whitespace-nowrap px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? isAll
                      ? isDark
                        ? "bg-primary text-primary-foreground"
                        : "bg-primary text-primary-foreground"
                      : getStatusColor(opt.value)
                    : isDark
                      ? "bg-muted/50 text-muted-foreground hover:bg-muted"
                      : "bg-muted/50 text-muted-foreground hover:bg-muted"
                }`}
              >
                {opt.label}
              </button>
            );
          });
        })()}
      </div>

      {renderTable()}
    </div>
  );
};
